import type { Lang } from "@/i18n/translations";

export type HomeCopy = {
  // Hero
  heroEyebrow: string;
  heroH1A: string;
  heroH1B: string;
  heroSub: string;
  heroTrust: string;
  ctaAsk: string;
  ctaRfq: string;
  ctaSupplier: string;
  chips: string[];

  // Hero flow card
  cardEyebrow: string;
  cardTitle: string;
  cardSub: string;
  heroFlow: string[]; // exactly 5 stages
  cardCta: string;
  cardFoot: string;

  // Section 2 — What to expect (4 cards)
  s2Eyebrow: string;
  s2Title: string;
  s2Sub: string;
  s2Cards: { title: string; body: string }[];

  // Section 3 — Problem
  s3Eyebrow: string;
  s3Title: string;
  s3Body: string;
  s3List: string[];
  s3Foot: string;

  // Section 4 — Solution (6-step process)
  s4Eyebrow: string;
  s4Title: string;
  s4Body: string;
  s4Steps: string[]; // 6 short labels

  // Section 5 — What buyers can source
  s5Eyebrow: string;
  s5Title: string;
  s5Sub: string;
  s5Categories: string[];
  s5Foot: string;

  // Section 6 — How it works (4 steps)
  s6Eyebrow: string;
  s6Title: string;
  s6Steps: { title: string; body: string }[];
  s6Cta: string;

  // Section 7 — Why Switzh (5 cards)
  s7Eyebrow: string;
  s7Title: string;
  s7Body: string;
  s7Cards: { title: string; body: string }[];

  // Section 8 — Supplier angle
  s8Eyebrow: string;
  s8Title: string;
  s8Body: string;
  s8Benefits: string[];
  s8Cta: string;

  // Section 9 — Trust
  s9Eyebrow: string;
  s9Title: string;
  s9Body: string;
  s9Items: string[];
  s9Callout: string;

  // Final CTA
  s10Eyebrow: string;
  s10Title: string;
  s10Body: string;
  s10Trust: string;
  s10Tertiary: string;

  // SEO
  seoTitle: string;
  seoDesc: string;
};

const en: HomeCopy = {
  heroEyebrow: "AI-Assisted Electrical Sourcing Across Borders",
  heroH1A: "Source U.S. switchgear and electrical components,",
  heroH1B: "faster.",
  heroSub:
    "Switzh turns part numbers, BOMs, and sourcing requests into structured RFQs — then coordinates supplier quotes, delivered pricing support, and cross-border logistics.",
  heroTrust:
    "AI-assisted intake. Human-reviewed sourcing. Delivered pricing support.",
  ctaAsk: "Ask Switzh",
  ctaRfq: "Submit RFQ",
  ctaSupplier: "For suppliers",
  chips: [
    "Structured RFQs",
    "Delivered pricing support",
    "Human-reviewed sourcing",
  ],

  cardEyebrow: "The Switzh Workflow",
  cardTitle: "From sourcing request to delivery",
  cardSub: "How a buyer request moves through Switzh.",
  heroFlow: [
    "Buyer request",
    "Structured RFQ",
    "Supplier quote",
    "Delivered pricing support",
    "Delivery",
  ],
  cardCta: "Ask Switzh – AI Sourcing Assistant",
  cardFoot: "Each request is reviewed by a sourcing specialist before quoting.",

  s2Eyebrow: "What to expect",
  s2Title: "What Switzh does",
  s2Sub:
    "A focused sourcing layer for buyers and a cleaner demand channel for U.S. electrical suppliers.",
  s2Cards: [
    {
      title: "Structured RFQs",
      body: "Turn messy requests, BOMs, SKUs, and product lists into quote-ready supplier requests.",
    },
    {
      title: "Supplier aggregation",
      body: "Route qualified requests through U.S. electrical supply channels.",
    },
    {
      title: "Delivered pricing support",
      body: "Support landed-cost visibility with logistics and cross-border delivery coordination.",
    },
    {
      title: "Human review",
      body: "AI helps structure the request. Human sourcing confirms fit, availability, alternatives, and next steps.",
    },
  ],

  s3Eyebrow: "The problem",
  s3Title: "Cross-border industrial procurement is still fragmented.",
  s3Body:
    "LATAM buyers often face slow supplier access, unclear landed costs, manual quote follow-up, and logistics complexity when sourcing U.S. electrical infrastructure products.",
  s3List: [
    "Local suppliers may be out of stock",
    "U.S. suppliers are hard to access directly",
    "BOMs and part requests require cleanup",
    "Delivered cost is often unclear",
    "Logistics coordination creates friction",
    "Urgent replacements cannot wait weeks",
  ],
  s3Foot:
    "For high-ticket electrical purchases, buyers need more than a product search. They need a reliable path from request to quote to delivery.",

  s4Eyebrow: "The solution",
  s4Title: "Switzh turns sourcing requests into structured procurement workflows.",
  s4Body:
    "AI-assisted intake. Human-reviewed sourcing. A clear path from buyer request to delivered quote.",
  s4Steps: [
    "Buyer submits SKU, BOM, part number, or project need",
    "Ask Switzh structures the request",
    "Human sourcing reviews fit and missing specs",
    "U.S. suppliers quote structured RFQs",
    "Switzh coordinates delivered pricing support and logistics",
    "Buyer moves from quote to order",
  ],

  s5Eyebrow: "What you can source",
  s5Title: "Built for critical electrical infrastructure sourcing.",
  s5Sub:
    "Switzh focuses on high-intent categories where supplier access, compatibility, and delivered pricing support matter most.",
  s5Categories: [
    "Components|Breakers, contactors, relays, fuses, disconnects, protection devices, control components.",
    "Power Distribution|Switchgear components, MCC buckets, panelboards, standard sections, transformers, power distribution equipment.",
    "Projects & Replacements|BOM sourcing, pre-configured power packages, hard-to-source replacement parts, urgent replacement needs.",
  ],
  s5Foot:
    "Strongest use cases: urgent replacements, active projects, BOM sourcing, and high-value infrastructure procurement.",

  s6Eyebrow: "How it works",
  s6Title: "From request to delivered quote in four steps.",
  s6Steps: [
    {
      title: "Ask Switzh",
      body: "Submit a SKU, BOM, product list, or sourcing request.",
    },
    {
      title: "We structure the RFQ",
      body: "AI organizes the request and identifies missing specs.",
    },
    {
      title: "Suppliers quote",
      body: "Qualified U.S. supplier channels receive structured requests.",
    },
    {
      title: "Delivered to LATAM",
      body: "Switzh supports final delivered pricing, payment flow, and logistics coordination.",
    },
  ],
  s6Cta: "Start with Ask Switzh",

  s7Eyebrow: "Why Switzh",
  s7Title: "Built to reduce procurement risk, not add another middleman.",
  s7Body:
    "For electrical infrastructure, buyers need correctness, compatibility, accountability, delivery reliability, and speed. Switzh is built around all five.",
  s7Cards: [
    {
      title: "Correctness",
      body: "Structured RFQs and human review reduce ambiguity before supplier outreach.",
    },
    {
      title: "Compatibility",
      body: "Exact-match and approved alternatives reviewed before quoting.",
    },
    {
      title: "Accountability",
      body: "Clear communication, quote records, and order visibility.",
    },
    {
      title: "Delivery reliability",
      body: "Delivered pricing and logistics coordination built into the workflow.",
    },
    {
      title: "Speed",
      body: "Organized RFQs help suppliers respond faster.",
    },
  ],

  s8Eyebrow: "For U.S. distributors",
  s8Title: "A cleaner demand channel for U.S. electrical suppliers.",
  s8Body:
    "Switzh helps U.S. suppliers and distributors capture structured LATAM demand without managing fragmented buyer intake, cross-border coordination, or unqualified quote noise.",
  s8Benefits: [
    "Qualified RFQs",
    "Cleaned part requests",
    "Buyer context and urgency",
    "Repeat sourcing categories",
    "Qualified commercial demand",
    "Cross-border coordination handled by Switzh",
  ],
  s8Cta: "Partner with Switzh",

  s9Eyebrow: "Trust & execution",
  s9Title: "Sourcing is only valuable if delivery works.",
  s9Body:
    "Switzh's advantage is not just finding supply. It is coordinating sourcing, reviewed quotes, delivered pricing support, and logistics into a single procurement workflow.",
  s9Items: [
    "Supplier documentation",
    "Clear invoice and payment structure",
    "Delivery visibility",
    "Human review",
    "Quote records",
    "Documented sourcing process",
    "Response speed",
    "Subject to supplier confirmation",
  ],
  s9Callout:
    "Final pricing, availability, alternatives, and delivery are subject to supplier and logistics confirmation before order approval.",

  s10Eyebrow: "Get started",
  s10Title: "Start with the part, BOM, or project need.",
  s10Body:
    "Ask Switzh helps structure your request before formal supplier review.",
  s10Trust:
    "Final pricing, availability, alternatives, and delivery are reviewed by a sourcing specialist.",
  s10Tertiary: "Partner with Switzh",

  seoTitle: "Switzh | Source U.S. Switchgear & Electrical Components Into LATAM",
  seoDesc:
    "Switzh helps LATAM industrial buyers source U.S. switchgear and electrical components through AI-assisted RFQs, supplier aggregation, delivered pricing, and cross-border logistics.",
};

const es: HomeCopy = {
  heroEyebrow: "Sourcing eléctrico asistido por IA entre fronteras",
  heroH1A: "Abastece switchgear y componentes eléctricos de EE. UU.,",
  heroH1B: "más rápido.",
  heroSub:
    "Switzh convierte números de parte, BOMs y solicitudes de sourcing en RFQs estructuradas — luego coordina cotizaciones de proveedores, soporte de precio entregado y logística transfronteriza.",
  heroTrust:
    "Captura asistida por IA. Sourcing revisado por humanos. Soporte de precio entregado.",
  ctaAsk: "Ask Switzh",
  ctaRfq: "Enviar RFQ",
  ctaSupplier: "Para proveedores",
  chips: [
    "RFQs estructuradas",
    "Soporte de precio entregado",
    "Sourcing revisado por humanos",
  ],

  cardEyebrow: "El flujo de Switzh",
  cardTitle: "De la solicitud a la entrega",
  cardSub: "Cómo se mueve una solicitud del comprador a través de Switzh.",
  heroFlow: [
    "Solicitud del comprador",
    "RFQ estructurada",
    "Cotización del proveedor",
    "Soporte de precio entregado",
    "Entrega",
  ],
  cardCta: "Ask Switzh – Asistente de Sourcing con IA",
  cardFoot:
    "Cada solicitud es revisada por un especialista de sourcing antes de cotizar.",

  s2Eyebrow: "Qué esperar",
  s2Title: "Lo que Switzh hace",
  s2Sub:
    "Una capa de sourcing enfocada para compradores y un canal de demanda más limpio para proveedores eléctricos de EE. UU.",
  s2Cards: [
    {
      title: "RFQs estructuradas",
      body: "Convierte solicitudes desordenadas, BOMs, SKUs y listas en pedidos listos para el proveedor.",
    },
    {
      title: "Agregación de proveedores",
      body: "Ruteo de solicitudes calificadas a canales de suministro eléctrico en EE. UU.",
    },
    {
      title: "Soporte de precio entregado",
      body: "Apoya la visibilidad del costo entregado con coordinación logística y transfronteriza.",
    },
    {
      title: "Revisión humana",
      body: "La IA estructura. El sourcing humano confirma fit, disponibilidad, alternativas y próximos pasos.",
    },
  ],

  s3Eyebrow: "El problema",
  s3Title: "El procurement industrial transfronterizo sigue fragmentado.",
  s3Body:
    "Los compradores en LATAM enfrentan acceso lento a proveedores, costos entregados poco claros, seguimiento manual de cotizaciones y complejidad logística al abastecer infraestructura eléctrica de EE. UU.",
  s3List: [
    "Proveedores locales pueden estar sin stock",
    "Es difícil acceder directamente a proveedores en EE. UU.",
    "BOMs y solicitudes requieren limpieza",
    "El costo entregado suele ser poco claro",
    "La coordinación logística genera fricción",
    "Los reemplazos urgentes no pueden esperar semanas",
  ],
  s3Foot:
    "Para compras eléctricas de alto valor, los compradores necesitan más que una búsqueda de productos. Necesitan un camino confiable de la solicitud a la cotización y a la entrega.",

  s4Eyebrow: "La solución",
  s4Title: "Switzh convierte solicitudes en flujos estructurados de procurement.",
  s4Body:
    "Captura asistida por IA. Sourcing revisado por humanos. Un camino claro de la solicitud a la cotización entregada.",
  s4Steps: [
    "El comprador envía SKU, BOM, número de parte o necesidad de proyecto",
    "Ask Switzh estructura la solicitud",
    "Sourcing humano revisa fit y datos faltantes",
    "Proveedores en EE. UU. cotizan RFQs estructuradas",
    "Switzh coordina soporte de precio entregado y logística",
    "El comprador avanza de cotización a pedido",
  ],

  s5Eyebrow: "Qué puedes abastecer",
  s5Title: "Diseñado para sourcing crítico de infraestructura eléctrica.",
  s5Sub:
    "Switzh se enfoca en categorías de alta intención donde el acceso a proveedores, la compatibilidad y el soporte de precio entregado importan más.",
  s5Categories: [
    "Componentes|Interruptores, contactores, relés, fusibles, desconectores, dispositivos de protección, componentes de control.",
    "Distribución de potencia|Componentes de switchgear, cubículos MCC, tableros, secciones estándar, transformadores, equipos de distribución.",
    "Proyectos y reemplazos|Sourcing por BOM, paquetes de potencia preconfigurados, repuestos difíciles de conseguir, reemplazos urgentes.",
  ],
  s5Foot:
    "Mejores casos de uso: reemplazos urgentes, proyectos activos, sourcing por BOM y compras de alto valor.",

  s6Eyebrow: "Cómo funciona",
  s6Title: "De la solicitud a la cotización entregada en cuatro pasos.",
  s6Steps: [
    {
      title: "Ask Switzh",
      body: "Envía un SKU, BOM, lista de productos o solicitud de sourcing.",
    },
    {
      title: "Estructuramos el RFQ",
      body: "La IA organiza la solicitud e identifica datos faltantes.",
    },
    {
      title: "Los proveedores cotizan",
      body: "Canales calificados de proveedores en EE. UU. reciben solicitudes estructuradas.",
    },
    {
      title: "Entregado a LATAM",
      body: "Switzh apoya el precio final entregado, el flujo de pago y la coordinación logística.",
    },
  ],
  s6Cta: "Empieza con Ask Switzh",

  s7Eyebrow: "Por qué Switzh",
  s7Title: "Construido para reducir riesgo de procurement, no para agregar otro intermediario.",
  s7Body:
    "En infraestructura eléctrica los compradores necesitan precisión, compatibilidad, responsabilidad, confiabilidad de entrega y velocidad. Switzh está construido alrededor de las cinco.",
  s7Cards: [
    {
      title: "Precisión",
      body: "RFQs estructuradas y revisión humana reducen la ambigüedad antes del contacto con el proveedor.",
    },
    {
      title: "Compatibilidad",
      body: "Equivalencias exactas y alternativas aprobadas son revisadas antes de cotizar.",
    },
    {
      title: "Responsabilidad",
      body: "Comunicación clara, registros de cotización y visibilidad del pedido.",
    },
    {
      title: "Confiabilidad de entrega",
      body: "Precio entregado y coordinación logística integrados en el flujo.",
    },
    {
      title: "Velocidad",
      body: "RFQs organizadas ayudan a los proveedores a responder más rápido.",
    },
  ],

  s8Eyebrow: "Para distribuidores en EE. UU.",
  s8Title: "Un canal de demanda más limpio para proveedores eléctricos en EE. UU.",
  s8Body:
    "Switzh ayuda a proveedores y distribuidores en EE. UU. a capturar demanda LATAM estructurada sin gestionar intake fragmentado, coordinación transfronteriza ni ruido de cotizaciones no calificadas.",
  s8Benefits: [
    "RFQs calificadas",
    "Solicitudes de parte limpias",
    "Contexto y urgencia del comprador",
    "Categorías de sourcing recurrentes",
    "Demanda comercial calificada",
    "Coordinación transfronteriza gestionada por Switzh",
  ],
  s8Cta: "Asóciate con Switzh",

  s9Eyebrow: "Confianza y ejecución",
  s9Title: "El sourcing solo es valioso si la entrega funciona.",
  s9Body:
    "La ventaja de Switzh no es solo encontrar suministro: es coordinar sourcing, cotizaciones revisadas, soporte de precio entregado y logística en un único flujo de procurement.",
  s9Items: [
    "Documentación del proveedor",
    "Estructura clara de factura y pago",
    "Visibilidad de entrega",
    "Revisión humana",
    "Registros de cotización",
    "Proceso de sourcing documentado",
    "Velocidad de respuesta",
    "Sujeto a confirmación del proveedor",
  ],
  s9Callout:
    "Precio final, disponibilidad, alternativas y entrega están sujetos a confirmación del proveedor y de la logística antes de aprobar el pedido.",

  s10Eyebrow: "Empieza ahora",
  s10Title: "Empieza con la parte, el BOM o la necesidad del proyecto.",
  s10Body:
    "Ask Switzh ayuda a estructurar tu solicitud antes de la revisión formal del proveedor.",
  s10Trust:
    "Precio final, disponibilidad, alternativas y entrega son revisadas por un especialista de sourcing.",
  s10Tertiary: "Asóciate con Switzh",

  seoTitle:
    "Switzh | Switchgear y componentes eléctricos de EE. UU. para LATAM",
  seoDesc:
    "Switzh ayuda a compradores industriales en LATAM a abastecer switchgear y componentes eléctricos de EE. UU. con RFQs asistidas por IA, agregación de proveedores, precio entregado y logística transfronteriza.",
};

const pt: HomeCopy = {
  heroEyebrow: "Sourcing elétrico assistido por IA entre fronteiras",
  heroH1A: "Abasteça switchgear e componentes elétricos dos EUA,",
  heroH1B: "mais rápido.",
  heroSub:
    "A Switzh transforma part numbers, BOMs e solicitações de sourcing em RFQs estruturadas — e depois coordena cotações de fornecedores, apoio ao preço entregue e logística transfronteiriça.",
  heroTrust:
    "Captura assistida por IA. Sourcing revisado por humanos. Apoio ao preço entregue.",
  ctaAsk: "Ask Switzh",
  ctaRfq: "Enviar RFQ",
  ctaSupplier: "Para fornecedores",
  chips: [
    "RFQs estruturadas",
    "Apoio ao preço entregue",
    "Sourcing revisado por humanos",
  ],

  cardEyebrow: "O fluxo da Switzh",
  cardTitle: "Da solicitação à entrega",
  cardSub: "Como uma solicitação de comprador percorre a Switzh.",
  heroFlow: [
    "Solicitação do comprador",
    "RFQ estruturada",
    "Cotação do fornecedor",
    "Apoio ao preço entregue",
    "Entrega",
  ],
  cardCta: "Ask Switzh – Assistente de Sourcing com IA",
  cardFoot:
    "Cada solicitação é revisada por um especialista de sourcing antes de cotar.",

  s2Eyebrow: "O que esperar",
  s2Title: "O que a Switzh faz",
  s2Sub:
    "Uma camada de sourcing focada para compradores e um canal de demanda mais limpo para fornecedores elétricos dos EUA.",
  s2Cards: [
    {
      title: "RFQs estruturadas",
      body: "Transforme solicitações desorganizadas, BOMs, SKUs e listas em pedidos prontos para o fornecedor.",
    },
    {
      title: "Agregação de fornecedores",
      body: "Roteamento de solicitações qualificadas para canais de suprimento elétrico nos EUA.",
    },
    {
      title: "Suporte de preço entregue",
      body: "Apoia a visibilidade do custo entregue com coordenação logística e transfronteiriça.",
    },
    {
      title: "Revisão humana",
      body: "A IA estrutura. O sourcing humano confirma fit, disponibilidade, alternativas e próximos passos.",
    },
  ],

  s3Eyebrow: "O problema",
  s3Title: "O procurement industrial transfronteiriço ainda é fragmentado.",
  s3Body:
    "Compradores na América Latina enfrentam acesso lento a fornecedores, custos entregues pouco claros, follow-up manual e complexidade logística ao buscar infraestrutura elétrica dos EUA.",
  s3List: [
    "Fornecedores locais podem estar sem estoque",
    "É difícil acessar fornecedores nos EUA diretamente",
    "BOMs e solicitações de peças exigem limpeza",
    "O custo entregue costuma ser pouco claro",
    "A coordenação logística gera fricção",
    "Substituições urgentes não podem esperar semanas",
  ],
  s3Foot:
    "Para compras elétricas de alto valor é preciso mais do que uma busca de produto. É preciso um caminho confiável da solicitação à cotação e à entrega.",

  s4Eyebrow: "A solução",
  s4Title:
    "A Switzh transforma solicitações de sourcing em fluxos estruturados de procurement.",
  s4Body:
    "Captura assistida por IA. Sourcing revisado por humanos. Um caminho claro da solicitação até a cotação entregue.",
  s4Steps: [
    "O comprador envia SKU, BOM, part number ou necessidade do projeto",
    "Ask Switzh estrutura a solicitação",
    "Sourcing humano revisa fit e dados faltantes",
    "Fornecedores nos EUA cotam RFQs estruturadas",
    "A Switzh coordena apoio ao preço entregue e logística",
    "O comprador avança de cotação para pedido",
  ],

  s5Eyebrow: "O que você pode abastecer",
  s5Title: "Construído para sourcing crítico de infraestrutura elétrica.",
  s5Sub:
    "A Switzh foca em categorias de alta intenção em que o acesso a fornecedores, a compatibilidade e o apoio ao preço entregue importam mais.",
  s5Categories: [
    "Componentes|Disjuntores, contatores, relés, fusíveis, seccionadores, dispositivos de proteção, componentes de comando.",
    "Distribuição de potência|Componentes de switchgear, cubículos MCC, painéis, seções padrão, transformadores, equipamentos de distribuição.",
    "Projetos e substituições|Sourcing por BOM, pacotes de potência pré-configurados, peças difíceis de encontrar, substituições urgentes.",
  ],
  s5Foot:
    "Melhores casos de uso: substituições urgentes, projetos ativos, sourcing por BOM e compras de alto valor.",

  s6Eyebrow: "Como funciona",
  s6Title: "Da solicitação à cotação entregue em quatro passos.",
  s6Steps: [
    {
      title: "Ask Switzh",
      body: "Envie um SKU, BOM, lista de produtos ou solicitação de sourcing.",
    },
    {
      title: "Estruturamos o RFQ",
      body: "A IA organiza a solicitação e identifica dados faltantes.",
    },
    {
      title: "Fornecedores cotam",
      body: "Canais qualificados de fornecedores nos EUA recebem solicitações estruturadas.",
    },
    {
      title: "Entregue na América Latina",
      body: "A Switzh apoia o preço final entregue, o fluxo de pagamento e a coordenação logística.",
    },
  ],
  s6Cta: "Comece com Ask Switzh",

  s7Eyebrow: "Por que Switzh",
  s7Title:
    "Construído para reduzir o risco de procurement — não para adicionar mais um intermediário.",
  s7Body:
    "Em infraestrutura elétrica os compradores precisam de correção, compatibilidade, responsabilidade, confiabilidade de entrega e velocidade. A Switzh é construída em torno das cinco.",
  s7Cards: [
    {
      title: "Correção",
      body: "RFQs estruturadas e revisão humana reduzem a ambiguidade antes do contato com o fornecedor.",
    },
    {
      title: "Compatibilidade",
      body: "Equivalências exatas e alternativas aprovadas são revisadas antes de cotar.",
    },
    {
      title: "Responsabilidade",
      body: "Comunicação clara, registros de cotação e visibilidade do pedido.",
    },
    {
      title: "Confiabilidade de entrega",
      body: "Preço entregue e coordenação logística integrados ao fluxo.",
    },
    {
      title: "Velocidade",
      body: "RFQs organizadas ajudam os fornecedores a responder mais rápido.",
    },
  ],

  s8Eyebrow: "Para distribuidores nos EUA",
  s8Title:
    "Um canal de demanda mais limpo para fornecedores elétricos nos EUA.",
  s8Body:
    "A Switzh ajuda fornecedores e distribuidores nos EUA a capturarem demanda estruturada da América Latina sem lidar com intake fragmentado, coordenação transfronteiriça ou ruído de cotações não qualificadas.",
  s8Benefits: [
    "RFQs qualificadas",
    "Solicitações de peças limpas",
    "Contexto e urgência do comprador",
    "Categorias de sourcing recorrentes",
    "Demanda comercial qualificada",
    "Coordenação transfronteiriça gerida pela Switzh",
  ],
  s8Cta: "Seja parceiro da Switzh",

  s9Eyebrow: "Confiança e execução",
  s9Title: "Sourcing só vale se a entrega funcionar.",
  s9Body:
    "A vantagem da Switzh não é apenas encontrar suprimento — é coordenar sourcing, cotações revisadas, apoio ao preço entregue e logística em um único fluxo de procurement.",
  s9Items: [
    "Documentação do fornecedor",
    "Estrutura clara de fatura e pagamento",
    "Visibilidade de entrega",
    "Revisão humana",
    "Registros de cotação",
    "Processo de sourcing documentado",
    "Velocidade de resposta",
    "Sujeito a confirmação do fornecedor",
  ],
  s9Callout:
    "Preço final, disponibilidade, alternativas e entrega estão sujeitos à confirmação do fornecedor e da logística antes da aprovação do pedido.",

  s10Eyebrow: "Comece agora",
  s10Title: "Comece pela peça, BOM ou necessidade do projeto.",
  s10Body:
    "Ask Switzh ajuda a estruturar sua solicitação antes da revisão formal pelo fornecedor.",
  s10Trust:
    "Preço final, disponibilidade, alternativas e entrega são revisadas por um especialista de sourcing.",
  s10Tertiary: "Seja parceiro da Switzh",

  seoTitle:
    "Switzh | Switchgear e componentes elétricos dos EUA para a América Latina",
  seoDesc:
    "A Switzh ajuda compradores industriais na América Latina a abastecer switchgear e componentes elétricos dos EUA com RFQs assistidos por IA, agregação de fornecedores, preço entregue e logística transfronteiriça.",
};

export const HOME_COPY: Record<Lang, HomeCopy> = { en, es, pt };
